import React from 'react';
import { Row, Col } from 'antd';
import Section from './section';
import BarChart from './barChart';
import SimpleReportTable, { SimpleReportTableColumn } from './simpleReportTable';
import styles from '../index.module.scss';

// 制动次数统计表数据项接口
interface BrakeCountItem {
    plate_number: string;
    aeb_ct_mileage: string;
    fcw_ct_mileage: string;
    hmw_ct_mileage: string;
    ldw_ct_mileage: string;
}

// 制动次数图表数据项接口
interface BrakeChartItem {
    date: string;
    AEB制动: number;
    FCW预警: number;
    HMW预警: number;
    急减速预警: number;
}

// 组件Props接口
interface DeviceBrakingStatisticsProps {
    tableData?: BrakeCountItem[];
    chartData?: BrakeChartItem[];
    analysisData?: DeviceBrakeAnalysis;
}

interface DeviceBrakeAnalysis {
    overall_trend?: string;
    top_vehicle_summary?: string;
    platform_overview_summary?: string;
    platform_comparison_summary?: string;
    management_advice_summary?: string;
}

// 设备制动次数与高频制动车辆统计模块
const DeviceBrakingStatistics: React.FC<DeviceBrakingStatisticsProps> = ({
    tableData = [],
    chartData = [],
    analysisData
}) => {
    // 将API数据转换为BarChart所需的格式
    const processChartData = (rawData: BrakeChartItem[]) => {
        if (!rawData || rawData.length === 0) {
            // 如果没有数据，返回空数组
            return [];
        }

        // 按制动类型分组，每个制动类型包含各个日期的数值
        const brakeTypes = ['AEB制动', 'FCW预警', 'HMW预警', '急减速预警'];

        return brakeTypes.map(brakeType => ({
            name: brakeType,
            value: rawData.map(item => [
                item.date,
                item[brakeType as keyof Omit<BrakeChartItem, 'date'>] || 0
            ] as [string, number])
        }));
    };

    const formattedChartData = processChartData(chartData);

    // 表格列定义
    const columns: SimpleReportTableColumn<BrakeCountItem>[] = [
        {
            title: '车牌号',
            dataIndex: 'plate_number',
            key: 'plate_number',
            width: 150,
        },
        {
            title: 'AEB制动百公里次数',
            dataIndex: 'aeb_ct_mileage',
            key: 'aeb_ct_mileage',
            width: 180,
        },
        {
            title: 'FCW预警百公里次数',
            dataIndex: 'fcw_ct_mileage',
            key: 'fcw_ct_mileage',
            width: 180,
        },
        {
            title: 'HMW预警百公里次数',
            dataIndex: 'hmw_ct_mileage',
            key: 'hmw_ct_mileage',
            width: 180,
        },
        {
            title: 'LDW预警百公里次数',
            dataIndex: 'ldw_ct_mileage',
            key: 'ldw_ct_mileage',
            width: 180,
        },
    ];

    const analysisItems = [
        { label: '总体趋势', value: analysisData?.overall_trend },
        { label: '单车制动情况趋势', value: analysisData?.top_vehicle_summary },
        { label: '平台平均值概览', value: analysisData?.platform_overview_summary },
        { label: '平台平均值对比分析', value: analysisData?.platform_comparison_summary },
        { label: '管控建议', value: analysisData?.management_advice_summary },
    ].filter(item => item.value);

    return (
        <Section title="十、设备制动次数与高频制动车辆统计">
            <>
                <Row gutter={24} style={{ minHeight: '420px' }}>
                    {/* 左侧柱状图 */}
                    <Col span={12}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            height: '100%',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>
                            <BarChart
                                data={formattedChartData}
                                title="设备制动次数统计对比"
                                xAxisLabel="日期"
                                yAxisLabel="次数"
                                style={{ width: '100%', height: '360px' }}
                                color={['#1890ff', '#52c41a', '#faad14', '#ff4d4f']}
                                isStacked={true}
                                showLegend={true}
                            />
                        </div>
                    </Col>

                    {/* 右侧数据表格 */}
                    <Col span={12}>
                        <div style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            height: '100%',
                            border: '1px solid #f0f0f0',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                        }}>

                            <SimpleReportTable
                                columns={columns}
                                dataSource={tableData}
                                rowKey="plate_number"
                                maxHeight={300}
                                scrollX
                                emptyText="暂无数据"
                            />
                        </div>
                    </Col>
                </Row>
                {analysisItems.length > 0 && (
                    <div className={styles.analysisBlock}>
                        <h4>分析结果</h4>
                        <div className={styles.analysisContent}>
                            {analysisItems.map(item => (
                                <p key={item.label}>
                                    <strong>{item.label}：</strong>{item.value}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </>
        </Section>
    );
};

export default DeviceBrakingStatistics;
